import { Head, router } from "@inertiajs/react"
import { ArrowLeft, Download, Copy, Check, ExternalLink } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import { coffeesPath, editCoffeePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

interface ScanEvent {
  scanned_at: string
  device_type: string | null
}

interface Props {
  coffee: {
    id: number
    name: string
    slug: string
    status: string
    qr_scans_count: number
    views_count: number
  }
  qr_svg: string
  tracking_url: string
  public_url: string
  scan_events: ScanEvent[]
}

export default function CoffeesQR({
  coffee,
  qr_svg,
  tracking_url,
  public_url,
  scan_events,
}: Props) {
  const { t } = useTranslation()
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: t("coffees.breadcrumb", "My Coffees"), href: coffeesPath() },
    { title: coffee.name, href: editCoffeePath(coffee.id) },
    { title: t("coffees.qr_code", "QR Code"), href: "#" },
  ]

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const downloadSVG = () => {
    const blob = new Blob([qr_svg], { type: "image/svg+xml" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${coffee.slug}-qr.svg`
    a.click()
    URL.revokeObjectURL(url)
  }

  const downloadPNG = (size: number) => {
    const svgElement = document.createElement("div")
    svgElement.innerHTML = qr_svg
    const svg = svgElement.querySelector("svg")
    if (!svg) return

    const canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const img = new Image()
    img.onload = () => {
      ctx.fillStyle = "white"
      ctx.fillRect(0, 0, size, size)
      ctx.drawImage(img, 0, 0, size, size)
      const a = document.createElement("a")
      a.href = canvas.toDataURL("image/png")
      a.download = `${coffee.slug}-qr-${size}px.png`
      a.click()
    }
    img.src =
      "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)))
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`QR Code - ${coffee.name}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => router.visit(editCoffeePath(coffee.id))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {t("coffees.qr_manager", "QR Code Manager")}
            </h1>
            <p className="text-muted-foreground text-sm">{coffee.name}</p>
          </div>
        </div>

        <div className="mx-auto grid w-full max-w-4xl gap-6 lg:grid-cols-2">
          {/* QR Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("coffees.qr_preview", "QR Code Preview")}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-4">
              <div
                className="rounded-lg bg-white p-4"
                dangerouslySetInnerHTML={{ __html: qr_svg }}
              />

              {coffee.status !== "published" && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {t(
                    "coffees.qr_not_published",
                    "Publish the coffee page first to make the QR code active.",
                  )}
                </p>
              )}

              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="outline" size="sm" onClick={downloadSVG}>
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  SVG
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadPNG(200)}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  200px
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadPNG(500)}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  500px
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadPNG(1000)}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  1000px
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* URLs & Stats */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("coffees.qr_links", "Links")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-muted-foreground mb-1 text-xs">
                    {t("coffees.public_page_url", "Public Page URL")}
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="bg-muted flex-1 truncate rounded px-2 py-1 text-sm">
                      {public_url}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => copyToClipboard(public_url, "public")}
                    >
                      {copiedField === "public" ? (
                        <Check className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    {coffee.status === "published" && (
                      <a
                        href={`/c/${coffee.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="icon-sm">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1 text-xs">
                    {t("coffees.tracking_url", "QR Tracking URL")}
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="bg-muted flex-1 truncate rounded px-2 py-1 text-sm">
                      {tracking_url}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => copyToClipboard(tracking_url, "tracking")}
                    >
                      {copiedField === "tracking" ? (
                        <Check className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("coffees.qr_stats", "Scan Statistics")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold">
                      {coffee.qr_scans_count}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {t("coffees.total_scans", "Total Scans")}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{coffee.views_count}</p>
                    <p className="text-muted-foreground text-xs">
                      {t("coffees.total_views", "Page Views")}
                    </p>
                  </div>
                </div>

                {scan_events.length > 0 && (
                  <div>
                    <p className="text-muted-foreground mb-2 text-xs font-medium">
                      {t("coffees.recent_scans", "Recent Scans")}
                    </p>
                    <div className="max-h-48 space-y-1.5 overflow-y-auto">
                      {scan_events.map((event, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between text-xs"
                        >
                          <span className="text-muted-foreground">
                            {new Date(event.scanned_at).toLocaleString()}
                          </span>
                          {event.device_type && (
                            <Badge variant="secondary" className="text-[10px]">
                              {event.device_type}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
